import React, { useState, useEffect, useRef } from "react";
import "./LeaveManagement.css";
import { motion, AnimatePresence } from "framer-motion";
import { FaRegEye, FaRegStickyNote, FaEdit, FaCheck, FaTimes } from "react-icons/fa";

const AbsentManagement = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCell, setHoveredCell] = useState(null);
  const [clickedCell, setClickedCell] = useState(null);
  const [academicYear, setAcademicYear] = useState(2025);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [newNotes, setNewNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const entriesPerPage = 15;
  const tooltipRef = useRef(null);

  const months = [
    "Jun", "Jul", "Aug", "Sep", "Oct", "Nov",
    "Dec", "Jan", "Feb", "Mar", "Apr", "May",
  ];

  // Production backend URL
  const API_BASE = "https://backend.ems.uk.hiradentallab.com";

  const fixDocumentUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("/")) {
      return `${API_BASE}${url}`;
    }
    return url;
  };

  const fetchLeaveData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/attendance/yearly/${academicYear}`);

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server Error ${response.status}: ${text.substring(0, 100)}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "API failed");
      }

      // Transform data from MonthlyAbsence collection
      const processedData = result.absences.map((record) => ({
        userId: record.userId,
        name: record.name,
        department: record.department,
        monthlyData: record.monthlyData || {},
      }));

      setLeaveData(processedData);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveData();
  }, [academicYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        const isAbsenceCell = event.target.closest(".has-absences");
        if (!isAbsenceCell) setClickedCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleNotesClick = (absence, userId, month) => {
    setSelectedAbsence({ ...absence, userId, month });
    setNewNotes(absence.notes || "");
    setShowNotesModal(true);
    setEditMode(false);
  };

  const handleSaveNotes = async () => {
    if (!selectedAbsence) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/attendance/absences/notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: selectedAbsence.userId,
          date: selectedAbsence.date,
          notes: newNotes.trim(),
        }),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "Failed to save notes");
      }

      setSuccessMessage("Notes saved successfully!");
      setShowNotesModal(false);
      await fetchLeaveData(); // Refresh table
    } catch (err) {
      setError("Could not save notes. Try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelNotes = () => {
    setShowNotesModal(false);
    setSelectedAbsence(null);
    setNewNotes("");
    setEditMode(false);
  };

  const handleCellHover = (e, userId, month, details) => {
    if (details?.length > 0) {
      let left = e.clientX + 20;
      if (left + 320 > window.innerWidth) left = e.clientX - 340;
      let top = e.clientY - 230;
      if (top < 0) top = e.clientY + 20;

      setHoveredCell({ userId, month, details, clientX: left, clientY: top });
    }
  };

  const handleCellClick = (e, userId, month, details) => {
    if (details?.length > 0) {
      let left = e.clientX + 20;
      if (left + 320 > window.innerWidth) left = e.clientX - 340;
      let top = e.clientY - 230;
      if (top < 0) top = e.clientY + 20;

      setClickedCell(
        clickedCell?.userId === userId && clickedCell?.month === month
          ? null
          : { userId, month, details, clientX: left, clientY: top }
      );
    }
  };

  const filteredData = leaveData
    .filter(
      (row) =>
        row.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.department?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const formatDate = (dateStr) => new Date(dateStr).toISOString().split("T")[0];

  return (
    <div className="leave-management">
      <div className="header-section">
        <div className="header-left">
          <h1 style={{ color: "#0353a4", marginTop: "60px", fontSize: "2rem" }}>
            Absent Management
          </h1>
          <span className="date-range">
            1 Jun {academicYear} - 31 May {academicYear + 1}
          </span>
          <hr />
        </div>
      </div>

      <AnimatePresence>
        {(successMessage || error) && (
          <motion.div
            className={successMessage ? "success-message" : "error-message"}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {successMessage || error}
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          Loading absence records...
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={fetchLeaveData}>Retry</button>
        </div>
      ) : (
        <div className="table-container">
          <div className="table-controls">
            <div className="date-picker">
              <button onClick={() => setAcademicYear((y) => y - 1)} className="arrow-btn">
                &lt;
              </button>
              <span className="year-range">{`${academicYear} - ${academicYear + 1}`}</span>
              <button onClick={() => setAcademicYear((y) => y + 1)} className="arrow-btn">
                &gt;
              </button>
            </div>
            <input
              type="text"
              placeholder="Search by name or department..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <table className="allowance-table">
            <thead>
              <tr>
                <th>Name</th>
                {months.map((m) => <th key={m}>{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {currentEntries.length > 0 ? (
                currentEntries.map((row) => (
                  <tr key={row.userId}>
                    <td>{row.name}</td>
                    {months.map((month) => {
                      const monthData = row.monthlyData[month] || { totalAbsences: 0, details: [] };
                      return (
                        <td
                          key={month}
                          className={monthData.totalAbsences > 0 ? "has-absences" : ""}
                          onMouseEnter={(e) => handleCellHover(e, row.userId, month, monthData.details)}
                          onMouseLeave={() => setHoveredCell(null)}
                          onClick={(e) => handleCellClick(e, row.userId, month, monthData.details)}
                        >
                          {monthData.totalAbsences}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={13} className="no-data">No records found</td>
                </tr>
              )}
            </tbody>
          </table>

          {filteredData.length > 15 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstEntry + 1}–{Math.min(indexOfLastEntry, filteredData.length)} of {filteredData.length}
              </div>
              <div className="pagination-buttons">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={currentPage === i + 1 ? "active" : ""}
                  >
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tooltip */}
      <AnimatePresence>
        {(hoveredCell || clickedCell) && (hoveredCell?.details?.length > 0 || clickedCell?.details?.length > 0) && (
          <motion.div
            ref={tooltipRef}
            className="absence-tooltip"
            style={{
              top: (clickedCell || hoveredCell).clientY,
              left: (clickedCell || hoveredCell).clientX,
              pointerEvents: clickedCell ? "auto" : "none",
            }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="tooltip-header">
              <h4>Absence Details</h4>
              <div className="tooltip-subheader">
                {(clickedCell || hoveredCell).month} - {(clickedCell || hoveredCell).details.length} day{(clickedCell || hoveredCell).details.length !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="tooltip-content">
              {(clickedCell || hoveredCell).details.map((absence, i) => (
                <div key={i} className="absence-detail">
                  <div className="absence-date">{formatDate(absence.date)}</div>
                  <div className="absence-actions">
                    {absence.document && (
                      <a href={fixDocumentUrl(absence.document)} target="_blank" rel="noopener noreferrer">
                        <FaRegEye />
                      </a>
                    )}
                    <button
                      onClick={() => handleNotesClick(absence, (clickedCell || hoveredCell).userId, (clickedCell || hoveredCell).month)}
                      title={absence.notes ? "Edit" : "Add Note"}
                    >
                      <FaRegStickyNote />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {clickedCell && <button className="close-tooltip-btn" onClick={() => setClickedCell(null)}>×</button>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes Modal */}
      <AnimatePresence>
        {showNotesModal && selectedAbsence && (
          <div className="modal-backdrop">
            <motion.div className="notes-modal-content" initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}>
              <div className="notes-modal-header">
                <h3>Notes for {formatDate(selectedAbsence.date)}</h3>
                <div className="notes-actions">
                  {!editMode ? (
                    <button onClick={() => setEditMode(true)}><FaEdit /></button>
                  ) : (
                    <>
                      <button onClick={handleSaveNotes} disabled={isSubmitting}><FaCheck /></button>
                      <button onClick={() => setEditMode(false)}><FaTimes /></button>
                    </>
                  )}
                  <button onClick={handleCancelNotes}>×</button>
                </div>
              </div>
              <div className="notes-modal-body">
                {editMode ? (
                  <textarea
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="Write notes here..."
                    rows={5}
                    autoFocus
                  />
                ) : newNotes ? (
                  <div className="notes-display">{newNotes}</div>
                ) : (
                  <div className="no-notes">No notes yet</div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AbsentManagement;